import {
    ActionIcon, Box,
    Button, Divider,
    Drawer,
    Group, Input, MultiSelect, Popover,
    Select,
    Text,
    Title,
    useCombobox,
    useMantineTheme
} from "@mantine/core";

import { ListViewOption } from "core/components/listToggle/listToggleControl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CSVGenerator, FilterButton, FormInputWrapper, StatusSelectBox } from "core/components";

import { logger } from "utils/logger";
import {
    IconEye,
    IconReload,
    IconSearch,
    IconTableExport,
    IconX,
} from "@tabler/icons-react";

import {
    filterSamples,
    generateColKeys, getClientLabel, pluralize,
    selectItems
} from "utils/helper";
import { SampleEventType } from "core/models/SampleModel";

import { EmptyState } from "core/components/emptyState/EmptyState";
import { openModal } from "@mantine/modals";

import { useForm } from "@mantine/form";
import CustomDatePicker from "core/components/datePicker/CustomDatePicker";
import moment from "moment";
import {
    selectStyles,
    setInventoryFilter,
    selectStyle,
    fetchInventories,
    loadInventories,
    setListColumns,
} from "modules/Inventory/inventorySlice";
import {
  normalizeInventory,
  filterByCreated,
  InventoryStatusTypes,
  PriorityTypesMap,
} from "modules/Inventory/inventoryStatus";


import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { WipFilterModel } from "core/models/WipFilterModel";
import {
    AppConstants, getCompanyTableColumns
} from "utils/constants";
import InventoryList from "./InventoryList";
import ColumnSelectPopover, { getColumnVisibility } from "core/components/columnSelectPopover/ColumnSelectPopover";
import IconSelectBox from "./IconSelectBox";
import InfoPanel from "./InfoPanel";

var _ = require("lodash");

// Values are column ids. "status" holds derivedStatus (see normalizeInventory)
// and "priority" is set by the slice on fetch.
const GROUP_BY_OPTIONS = [
  { value: "deptName", label: "Group by Department" },
  { value: "productCategory", label: "Group by Product Category" },
  { value: "size", label: "Group by Size" },
  { value: "derivedStatus", label: "Group by Status" },
  { value: "priority", label: "Group by Priority" },
];

const Inventories = ({
  inventories,
  inventoriesError,
  selectedStyles,
  setInventoryFilter,
  inventoryFilter,
  selectStyles,
  selectStyle,
  selectedStyle,
  listColumns,
  user,
}) => {
  const dispatch = useDispatch();
  const [filterOpen, setFilterOpen] = useState(false);
  const [listView, setListView] = useState(ListViewOption.LIST);
  const navigate = useNavigate();
  const location = useLocation();
  // const [selectedItems, setSelectedItems] = useState(selectedStyles);
  const [filterCount, setFilterCount] = useState(0);
  const [filterItems, setFilterItems] = useState(inventoryFilter);
  const [searchParams, setSearchParams] = useSearchParams();
  const [opened, { open, close }] = useDisclosure(false);

  const wips = useMemo(
    () => (inventories?.data ?? []).map(normalizeInventory),
    [inventories?.data]
  );
  const [wipData, setWipData] = useState(wips);

  // Pages land progressively; show the skeleton until the last one is in.
  const isLoading = !inventories?.complete && !inventoriesError;
  const theme = useMantineTheme();
  const tabletMatch = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const selectAllMenu = (
    <div>
      <Button
        variant="default"
        onClick={(e) => onMenuItemClick(SampleEventType.SAMPLE_DISTRIBUTE)}
      >
        Distribute
      </Button>
      <Button variant="default" onClick={(e) => onSelectAll(e)}>
        Deselect All
      </Button>
    </div>
  );
  const columns = user?.clientCode?getCompanyTableColumns(user.clientCode, "inventories"):[];
  // Which columns are shown: the user's saved choices over the defaults.
  const columnVisibility = getColumnVisibility(columns, listColumns);

  // Grouping is by column id, so only offer groups whose column this client's
  // list has (e.g. only TAR has a priority column).
  const columnIds = columns.map((col) => col.id ?? col.accessorKey);
  const groupByOptions = [
    { value: "", label: "No Group" },
    ...GROUP_BY_OPTIONS.filter((option) => columnIds.includes(option.value)),
  ];

  const filterWips = useCallback(() => {
    // Only records created within the date range (the dashboard's date filter).
    // Priorities saved before P0-P8 (e.g. "CRITICAL") are dropped rather
    // than matching nothing.
    const knownPriorities = (inventoryFilter.priorities ?? []).filter((value) =>
      priorities.some((type) => type.value === value)
    );
    var data = filterSamples(wips, { ...inventoryFilter, priorities: knownPriorities }, columns)

    setWipData(data);
  }, [
    inventoryFilter.brands,
    inventoryFilter.categories,
    setWipData,
    wips,
    inventoryFilter.groupBy,
    inventoryFilter.keyword,
    inventoryFilter.statuses,
    inventoryFilter.priorities,
    inventoryFilter.dataFilter,
    inventoryFilter.departments,
    inventoryFilter.sizes,
    inventoryFilter.productCategories,
    inventoryFilter.productSubcategories,
  ]);

  /**
   * Effect to handle location change
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let displayType = params.get("displayType");

    const styleNumber = params.get("styleNumber");

    // open the drawer if we have the style
    if (styleNumber && wips.length) {
      const style = wips.find((val) => val.styleNumber === styleNumber);
      // Other URL changes (e.g. the image viewer's layoutType) re-run this;
      // re-selecting the raw record would drop the style info InfoPanel
      // fetched (masters, sessions, assets), so only select a new style.
      if (style) {
        if (selectedStyle?.styleNumber !== styleNumber) {
          dispatch(selectStyle(style));
          dispatch(selectStyles([style]));
        }
        open();
      }
    }

    logger.log("location change ", location, " DISPLAY: ", displayType, params);
    // if display type changes
    if (displayType && displayType !== listView) setListView(displayType);
  }, [location, listView, wips]);

  /**
   * Handles the list toggle display
   * @param {*} val
   */
  const listToggleHandler = (val) => {
    navigate({ search: `?displayType=${val}` });
  };

  // Inventory isn't week-scoped, so load every page once. Reuse what the
  // dashboard (or a previous visit) already loaded while it's fresh.
  useEffect(() => {
    dispatch(loadInventories(AppConstants.CACHE_DELAY_LONG));
  }, [dispatch]);

  useEffect(() => {
    logger.log("setting wips, ", wips);
    //setWipData(wips);

    // set the brands/season/category
    var styleNumbers = {};
    var sessions = {};
    var seasons = {};
    var statuses = {};
    var talents = {};
    var issuers = {};
    var depts = {};
    var sizes = {};
    // Product category -> its subcategories, so the filter panel can offer
    // only the subcategories of the selected categories.
    var productCategories = {};

    wips?.forEach((item, idx) => {
      styleNumbers[item.styleNumber] = item;
      sessions[item.sessionCode] = item;
      seasons[item.seasonCode] = item;
      // statuses[item.status] = item;
      talents[item.talentNames] = item;
      issuers[item.issuer] = item;
      depts[item.dept] = item;
      sizes[item.size] = item;
      if (item.productCategory) {
        productCategories[item.productCategory] ??= new Set();
        if (item.productSubcategory) {
          productCategories[item.productCategory].add(item.productSubcategory);
        }
      }
    });

    setFilterItems({
      styleNumbers,
      sessions,
      seasons,
      // statuses,
      talents,
      depts,
      issuers,
      sizes,
      productCategories,
    });

    filterWips();
  }, [wips, filterWips]);

  const onItemClick = (row) => {
    // we need to select the current wip
    // dispatch(selectWip(item));

    // update the route
    // navigate(`/warehouse/wips/${item.wipNumber}`);
    if (row.subRows && row.subRows.length) {
    } else {
      // Re-clicking the open style keeps its fetched info.
      if (selectedStyle?.styleNumber !== row.original.styleNumber) {
        dispatch(selectStyle(row.original));
      }
      // dispatch(selectStyles([row.original]));

      searchParams.set("styleNumber", row.original.styleNumber);
      let urlParms = Object.fromEntries([...searchParams]);
      urlParms.styleNumber = row.original.styleNumber;
      setSearchParams(urlParms);

      // navigate(`/warehouse/wips/${item.wipNumber}`);

      // open();
    }

    logger.log("on item click");
  };

  const onItemSelected = (e, item, selected, rows) => {
    logger.log(item, selectedStyles);

    var items = [...selectedStyles];

    // if the item is a group then push the elements into the group.
    if (item.type && item.type === "group") {
      if (selected) {
        items.push({ id: item.id });
        item.subRows.forEach((row) => {
          var tmpItem = items.find((val) => val.id === row.id);
          if (!tmpItem) items.push({ id: row.id });
        });
      } else {
        _.remove(items, (val) => {
          return val.id === item.id;
        });

        item.subRows.forEach((row) => {
          _.remove(items, (val) => {
            return val.id === row.id;
          });
        });
      }
    } else {
      // var items = [...selectedSeasons];

      // call general helper to select items
      selectItems(
        e,
        item,
        selected,
        rows.map((val) => val.original),
        items
      );
    }
    selectStyles(items);
  };

  const onSelectAll = () => {
    // if there are items selected remove them
    if (selectedStyles.length) {
      //setSelectedItems([])
      dispatch(selectStyles([]));
    } else {
      //setSelectedItems([...elements])
      dispatch(selectStyles([...wipData]));
    }
  };

  const onMenuItemClick = (e) => {
    logger.log(e);
    switch (e) {
      case SampleEventType.SAMPLE_BUILD_OUTFIT: {
        navigate(`/warehouse/outfitter/create`);
        break;
      }
      case SampleEventType.SAMPLE_DISTRIBUTE: {
        openModal({
          title: `New Distribution (${selectedStyles.length})`,
          size: "80%",
          children: (
            <>
              {/* <Distribution selectedItems={selectedStyles.map((val) => wips.find((wip) => wip.id === val.id))} /> */}
            </>
          ),
        });
        break;
      }
      case SampleEventType.SAMPLE_VIEW: {
        onItemClick(e.data);
        break;
      }
      default: {
        break;
      }
    }
  };

  const setFilterVars = (vals) => {
    logger.log(vals);
    setInventoryFilter(vals);
  };

  useEffect(() => {
    // filter the data.
    filterWips();
  }, [filterWips]);

  const getFilterCount = (val) => {
    var count = 0;
    const dateKeys = ["otbDate", "distribution", "completed"];
    const ignoreKeys = ["groupBy"];
    Object.entries(val).forEach(([key, value]) => {
      if (value && value.length && ignoreKeys.indexOf(key) === -1) {
        ++count;
      }

      if (key === "status") {
        var statusCount = 0;
        Object.entries(value).forEach(([key, value]) => {
          if (value.length) {
            statusCount = 1;
          }
        });

        count += statusCount;
      }

      if (key === "otbDate") {
        if (value.dateStart.toString() || value.dateEnd.toString()) {
          ++count;
        }
      }

      if (key === "completed") {
        if (value.dateStart.toString() || value.dateEnd.toString()) {
          ++count;
        }
      }

      if (key === "distribution") {
        if (value.dateStart.toString() || value.dateEnd.toString()) {
          ++count;
        }
      }

      // if (dateKeys.indexOf(key) > -1) {
      //   if (isDate(value.dateStart) || isDate(value.dateEnd)) {
      //     ++count;
      //   }
      // }
    });

    return count;
  };

  useEffect(() => {
    const count = getFilterCount(inventoryFilter);
    setFilterCount(count);
  }, [inventoryFilter]);

  const onClearClick = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopPropagation();

    setInventoryFilter({ ...new WipFilterModel({ groupBy: inventoryFilter.groupBy }) });

    logger.log("CLEAR CLICK", e);
  };

  const filterButtonHandler = (e) => {
    logger.log("BUTTON CLICK", e, e.nativeEvent);
    setFilterOpen((o) => !o);
  };

  const rowMenuItems = [
    {
      handler: (data) => {
        onItemClick(data);
      },
      label: "View Wip",
      icon: <IconEye size={14} />,
    },
  ];

  useEffect(() => {
    if (inventoriesError) {
      logger.log("Inventory fetch error", inventoriesError);
    }
  }, [inventoriesError]);

  logger.log(inventoryFilter);
  const reloadHandler = () => {
    dispatch(fetchInventories({}));
  };
  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Group
        className="main-container-header"
        justify="space-between"
        align="center"
      >
        <Title order={3} lh={"lg"}>
          Inventories
        </Title>

        <Group>
          <Text size="xs" c={"dimmed"} visibleFrom="sm">
            Last updated {moment(inventories?.timestamp).fromNow()}
          </Text>
          <Button
            variant="default"
            onClick={() => reloadHandler()}
            leftSection={
              <IconReload
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            }
          >
            Reload
          </Button>
          <Button
            variant="default"
            size="sm"
            leftSection={
              <IconTableExport
                stroke={AppConstants.ICON_STROKE_WIDTH}
                size={AppConstants.ICON_SIZE}
              />
            }
            sx={{ textAlign: "left" }}
            // onClick={(e) => onMenuItemClick(BulkActions.EXPORT)}
          >
            <CSVGenerator
              data={wipData ?? []}
              columns={generateColKeys(columns)}
              filename="inventory-export"
              // ignoreKeys={generateIgnoreKeys(columns,wipData)}
            />
          </Button>
        </Group>

        {/* <ListToggleControl
          value={listView}
          onChange={(val) => listToggleHandler(val)}
          types={[
            {
              label: (
                <Center>
                  <IconList size={16} />
                </Center>
              ),
              value: "list",
            },
            {
              label: (
                <Center>
                  <IconCalendar size={16} />
                </Center>
              ),
              value: "calendar",
            },
          ]}
        /> */}
      </Group>

      <Group
        className="container-action-bar"
        style={{ width: "100%" }}
        justify="space-between"
        height={"100%"}
        py="xs"
      >
        <Group>
          {/* <CheckboxDropdown selectedItems={selectedStyles} onCheckboxClick={onSelectAll} selectMenu={selectAllMenu} /> */}
          <Input
            leftSection={<IconSearch size={AppConstants.ICON_SIZE} />}
            style={{ width: "300px" }}
            value={inventoryFilter.keyword}
            onChange={(e) =>
              setInventoryFilter({
                ...inventoryFilter,
                keyword: e.currentTarget.value,
              })
            }
            placeholder={`Search across ${wips.length} ${pluralize(
              "Record",
              wips.length
            )}`}
            rightSectionPointerEvents="all"
            rightSection={
              <ActionIcon
                variant="transparent"
                onClick={() =>
                  setInventoryFilter({
                    ...inventoryFilter,
                    keyword: "",
                  })
                }
              >
                {inventoryFilter.keyword.length ? <IconX size={14} /> : null}
              </ActionIcon>
            }
          />
        </Group>
        <Group>
          {/* {listView !== ListViewOption.CALENDAR ? (
            <Group grow={0}>
              <Popover
                shadow="sm"
                opened={dateFilterOpen}
                onClose={() => setDateFilterOpen(false)}
                width={480}
                position="bottom"
                withArrow
                closeOnClickOutside={true}
                onChange={setDateFilterOpen}
              >
                <Popover.Target>
                  <div>
                    <Button
                      variant="light"
                      onClick={() => setDateFilterOpen((o) => !o)}
                      px={"xs"}
                    >
                      <Group>
                        <Badge color="green" variant="light">
                          {inventoryFilter.dataFilter.dateRange.item.label}
                        </Badge>
                        <Text size={"sm"}>
                          <Moment format="D MMM YYYY">{dateRange[0]}</Moment> -{" "}
                          <Moment format="D MMM YYYY">{dateRange[1]}</Moment>
                        </Text>
                        <IconChevronDown size={14} />
                      </Group>
                    </Button>
                  </div>
                </Popover.Target>
                <Popover.Dropdown>
                  <div style={{ display: "flex" }}>
                    <DateFilterPanel
                      showTimePicker={false}
                      filter={inventoryFilter.dataFilter}
                      onCancel={() => setDateFilterOpen(false)}
                      onApply={(vals) => {
                        let urlParms = Object.fromEntries([...searchParams]);
                        // const urlParms = new URLSearchParams(window.location.search);
                        var startDate = vals.dateRange.value[0];
                        startDate.setHours(0, 0, 0, 0);

                        // if it's past the current date set it today
                        const today = getDateRanges({
                          value: DateRanges.TODAY,
                        });
                        if (startDate > today.startDate) {
                          startDate = today.startDate;
                        }

                        const formattedDate =
                          moment(startDate).format("YYYY-MM-DD");

                        // set the settings as part of the url.
                        searchParams.set("weekstart", formattedDate);
                        urlParms.weekstart = formattedDate;
                        setSearchParams(urlParms);

                        setDateFilterOpen(false);
                        setDoFetch(true);
                      }}
                    />
                  </div>
                </Popover.Dropdown>
              </Popover>
            </Group>
          ) : null} */}
          <Popover
            shadow="sm"
            opened={filterOpen}
            onChange={setFilterOpen}
            width={tabletMatch ? "100%" : 610}
            withArrow
            closeOnClickOutside={true}
            position="bottom-end"
            // onClose={() => setFilterOpen(false)}
          >
            <Popover.Target>
              <div>
                <FilterButton
                  onClick={filterButtonHandler}
                  onClear={onClearClick}
                  count={filterCount}
                />
              </div>
            </Popover.Target>
            <Popover.Dropdown>
              <div wip={{ display: "flex" }}>
                <FilterPanel
                  filter={inventoryFilter}
                  onCancel={() => setFilterOpen(false)}
                  onApply={(vals) => {
                    setFilterVars(vals);
                    setFilterOpen(false);
                  }}
                  filterItems={filterItems}
                  user={user}
                />
              </div>
            </Popover.Dropdown>
          </Popover>
          
          {listView !== ListViewOption.CALENDAR ? (
            <Select
              placeholder="Group By"
              w={230}
              clearable
              value={inventoryFilter.groupBy}
              onChange={(val) => setInventoryFilter({ ...inventoryFilter, groupBy: val })}
              data={groupByOptions}
            />
          ) : null}

          {listView === ListViewOption.LIST ? (
            <ColumnSelectPopover
              columns={columns}
              visibility={columnVisibility}
              onChange={(value) => dispatch(setListColumns(value))}
              onReset={() => dispatch(setListColumns(null))}
            />
          ) : null}
          
        </Group>
      </Group>

      {/* <ScrollArea wip={{height:500}} > */}
      <Box
        className={
          inventoryFilter.groupBy && inventoryFilter.groupBy.length
            ? "main-container-content show-data-grouped"
            : "main-container-content show-data-grouped-hidden"
        }
        style={{ flex: 1, marginBottom: 33, flex: 1, overflow: "hidden" }}
      >
        {listView === ListViewOption.LIST ? (
          <div className="table-wrapper" style={{ position: "relative" }}>
            <InventoryList
              originalData={wips}
              loading={isLoading}
              data={wipData}
              onItemClick={onItemClick}
              onMenuItemClick={onMenuItemClick}
              onItemSelected={onItemSelected}
              selectedItems={selectedStyles}
              filter={inventoryFilter}
              actionMenu={rowMenuItems}
              columns={columns}
              columnVisibility={columnVisibility}
              setFilter={setInventoryFilter}
            />

            {wipData && wipData.length === 0 && !isLoading ? (
              <Box
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 200,
                }}
              >
                <EmptyState />
              </Box>
            ) : null}

            {/* <TestList wipData={wips} /> */}
          </div>
        ) : (
          //     <SampleList
          //         loading={isLoading}
          //         data={wipData}
          //         onItemClick={onItemClick}
          //         onMenuItemClick={onMenuItemClick}
          //         onItemSelected={onItemSelected}
          //         selectedItems={selectedStyles}
          //         filter={inventoryFilter}
          //         actionMenu={rowMenuItems}
          //         columns={[
          //             {
          //                 // Build our expander column
          //                 id: 'expander', // Make sure it has an ID
          //                 Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
          //                     <>
          //                         {/* <span {...getToggleAllRowsExpandedProps()}>
          //   {isAllRowsExpanded ? <IconChevronDown /> : <IconChevronRight />}
          // </span> */}
          //                     </>
          //                 ),
          //                 Cell: ({ row }) =>
          //                     // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
          //                     // to build the toggle for expanding a row
          //                     row.canExpand ? (
          //                         <span
          //                             {...row.getToggleRowExpandedProps({
          //                                 wip: {
          //                                     // We can even use the row.depth property
          //                                     // and paddingLeft to indicate the depth
          //                                     // of the row
          //                                     paddingLeft: `${row.depth * 2}rem`,
          //                                 },
          //                             })}>
          //                             {row.isExpanded ? <IconChevronDown /> : <IconChevronRight />}
          //                         </span>
          //                     ) : null,
          //             },
          //             {
          //                 Header: 'Photo Code',
          //                 accessor: 'photoCode',
          //             },
          //             {
          //                 Header: 'Wip',
          //                 accessor: 'wipNumber',
          //             },
          //             {
          //                 Header: 'Status',
          //                 accessor: 'status',
          //             },
          //             {
          //                 Header: 'Name',
          //                 accessor: 'name',
          //             },
          //             {
          //                 Header: 'Event',
          //                 accessor: 'seasonCode',
          //             },
          //             {
          //                 Header: 'Session',
          //                 accessor: 'sessionCode',
          //             },
          //             {
          //                 Header: 'Department',
          //                 accessor: 'dept',
          //             },
          //             {
          //                 Header: 'OTB Date',
          //                 accessor: 'otbDate',
          //                 sortType: (a, b) => headerDateSort(a, b, 'otbDate'),
          //             },
          //             {
          //                 Header: 'Completed',
          //                 accessor: 'completed',
          //                 sortType: (a, b) => headerDateSort(a, b, 'completed'),
          //             },
          //             {
          //                 Header: 'Dist Date',
          //                 accessor: 'distributionDate',
          //                 sortType: (a, b) => headerDateSort(a, b, 'distributionDate'),
          //             },
          //             {
          //                 Header: 'Issuer',
          //                 accessor: 'issuer',
          //                 width: 200,
          //             },
          //             {
          //                 Header: '',
          //                 accessor: 'action',
          //                 width: 50,
          //             },
          //             //   ],
          //             // },
          //         ]}
          //         setFilter={setInventoryFilter}
          //     />
          // <WipGrid
          //     data={wipData}
          //     onItemClick={onItemClick}
          //     onItemSelected={onItemSelected}
          //     selectedItems={selectedStyles}
          // />
          <>{/* <WipCalendar styles={wipData} /> */}</>
        )}
      </Box>
      {/* </ScrollArea> */}

      <Drawer
        id="drawer"
        opened={opened}
        closeOnEscape={false}
        trapFocus={true}
        onClose={() => {
          let urlParms = Object.fromEntries([...searchParams]);
          delete urlParms.styleNumber;
          setSearchParams(urlParms);
          close();
        }}
        title="Quick Style View"
        position="right"
      >
        {/* Drawer content */}
        <InfoPanel selectedItem={selectedStyle} opened={opened} doFetch={true} user={user} />
      </Drawer>
    </Box>
  );
};

const mapStateToProps = (state, props) => {
  //logger.log(state,props, ' mapstate')
  return {
    user: state.auth.user,
    inventoryFilter: state.inventoryReducer.inventoryFilter,
    selectedStyles: state.inventoryReducer.selectedStyles,
    selectedStyle: state.inventoryReducer.selectedStyle,
    inventories: state.inventoryReducer.inventories,
    inventoriesError: state.inventoryReducer.inventoriesError,
    displayPrefs: state.inventoryReducer.displayPrefs,
    listColumns: state.inventoryReducer.listColumns,
  };
};

const mapDispatchToProps = { setInventoryFilter, selectStyles, selectStyle };

export default connect(mapStateToProps, mapDispatchToProps)(Inventories);

const FilterPanel = ({ onCancel, onApply, filter, filterItems, user }) => {

  const form = useForm({
    initialValues: {
      ...filter,
      // completed: { ...filter.completed },
      // otbDate: { ...filter.otbDate },
      completed: {
        dateStart: filter.completed?.dateStart?.toString().length
          ? moment(filter.completed?.dateStart).toDate()
          : "",
        dateEnd: filter.completed?.dateEnd?.toString().length
          ? moment(filter.completed?.dateEnd).toDate()
          : "",
      },
      otbDate: {
        dateStart: filter.otbDate?.dateStart?.toString().length
          ? moment(filter.otbDate?.dateStart).toDate()
          : "",
        dateEnd: filter.otbDate?.dateEnd?.toString().length
          ? moment(filter.otbDate?.dateEnd).toDate()
          : "",
      },
      distribution: {
        dateStart: filter.distribution?.dateStart?.toString().length
          ? moment(filter.distribution?.dateStart).toDate()
          : "",
        dateEnd: filter.distribution?.dateEnd?.toString().length
          ? moment(filter.distribution?.dateEnd).toDate()
          : "",
      },
      // distributionDate: { ...filter.distribution },
      // status:{
      //     sampleCount:filter.status.sampleCount.toString(),
      //     photographyCount:filter.status.photographyCount.toString(),
      //     taskCount:filter.status.taskCount.toString(),
      //     taskCompletedCount:filter.status.taskCompletedCount.toString()
      // }
    },
  });

  // useEffect(() => {
  //   if (filter && form) {
  //     form.setValues({ ...filter });
  //   }
  // }, [filter]);

  const getFilterVars = useCallback(
    (key) => {
      var res = [];
      if (filterItems[key])
        Object.keys(filterItems[key]).forEach((val) => res.push(val));
      return res.sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true })
      );
    },
    [filter, filterItems]
  );

  // Subcategories of the selected product categories only.
  const selectedCategories = form.values.productCategories ?? [];
  const subcategoryOptions = useMemo(
    () =>
      [
        ...new Set(
          selectedCategories.flatMap((category) => [
            ...(filterItems.productCategories?.[category] ?? []),
          ])
        ),
      ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [selectedCategories, filterItems.productCategories]
  );

  // Changing the categories drops any picked subcategory that no longer
  // belongs to one of them (and all of them when no category is picked).
  const setProductCategories = (categories) => {
    form.setFieldValue("productCategories", categories);
    const allowed = new Set(
      categories.flatMap((category) => [
        ...(filterItems.productCategories?.[category] ?? []),
      ])
    );
    form.setFieldValue(
      "productSubcategories",
      (form.values.productSubcategories ?? []).filter((value) => allowed.has(value))
    );
  };

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  return (
    <Box shadow="xs" wip={{ width: "100%" }} className="filter-panel">
      <form onSubmit={form.onSubmit((values) => onApply(values))}>
        {/* <ScrollArea wip={{ overflowX: "hidden" }}> */}
        <Box>
          <FormInputWrapper label="Styles" labelWidth={"120px"}>
            <MultiSelect
              data={getFilterVars("styleNumbers")}
              label=""
              placeholder="Pick all that you like"
              searchable
              comboboxProps={{ withinPortal: false }}
              // nothingFound="Nothing found"
              {...form.getInputProps("styleNumbers")}
            />
          </FormInputWrapper>

          <FormInputWrapper label="Department" labelWidth={"120px"}>
            <MultiSelect
              data={getFilterVars("depts")}
              label=""
              placeholder="Pick all that you like"
              searchable
              comboboxProps={{ withinPortal: false }}
              // nothingFound="Nothing found"
              {...form.getInputProps("departments")}
            />
          </FormInputWrapper>
          <Divider />
          <FormInputWrapper label="Category" labelWidth={"120px"}>
            <MultiSelect
              data={getFilterVars("productCategories")}
              label=""
              placeholder="Pick all that you like"
              searchable
              comboboxProps={{ withinPortal: false }}
              value={selectedCategories}
              onChange={setProductCategories}
            />
          </FormInputWrapper>

          {selectedCategories.length ? (
            <FormInputWrapper label="Sub Category" labelWidth={"120px"}>
              <MultiSelect
                data={subcategoryOptions}
                label=""
                placeholder="Pick all that you like"
                searchable
                comboboxProps={{ withinPortal: false }}
                {...form.getInputProps("productSubcategories")}
              />
            </FormInputWrapper>
          ) : null}
<Divider />
          <FormInputWrapper label="Size" labelWidth={"120px"}>
            <MultiSelect
              data={getFilterVars("sizes")}
              label=""
              placeholder="Pick all that you like"
              searchable
              comboboxProps={{ withinPortal: false }}
              // nothingFound="Nothing found"
              {...form.getInputProps("sizes")}
            />
          </FormInputWrapper>

          <FormInputWrapper
            label={getClientLabel(user.clientCode, 'otbDate')}
            id="input-otbDate"
            labelWidth={"120px"}
          >
            <Group wrap={"nowrap"}>
              <CustomDatePicker
                sx={{ width: 190 }}
                placeholder={`From ${getClientLabel(user.clientCode, 'otbDate')}`}
                {...form.getInputProps("otbDate.dateStart")}
              />
              <Text>to</Text>
              <CustomDatePicker
                sx={{ width: 190 }}
                placeholder={`To ${getClientLabel(user.clientCode, 'otbDate')}`}
                {...form.getInputProps("otbDate.dateEnd")}
              />
            </Group>
          </FormInputWrapper>

          <FormInputWrapper label="Status" labelWidth={"120px"}>
            <StatusSelectBox searchable={false} defaultValue={form.values.statuses} data={statuses} onChange={(val) => {
              form.setFieldValue("statuses", val);
            }} />

          </FormInputWrapper>

          <FormInputWrapper label="Priority" labelWidth={"120px"}>
            <IconSelectBox
              data={priorities}
              placeholder="Pick all that you like"
              value={form.values.priorities ?? []}
              onChange={(val) => form.setFieldValue("priorities", val)}
            />
          </FormInputWrapper>
          {/* <FormInputWrapper
            label="Completed Date"
            id="input-completed"
            labelWidth={"120px"}
          >
            <Group wrap={"nowrap"}>
              <CustomDatePicker
                sx={{ width: 190 }}
                placeholder="From Completed Date"
                {...form.getInputProps("completed.dateStart")}
              />

              <Text>to</Text>
              <CustomDatePicker
                sx={{ width: 190 }}
                placeholder="To Completed Date"
                {...form.getInputProps("completed.dateEnd")}
              />
            </Group>
          </FormInputWrapper>

          <FormInputWrapper
            label="Distribution Date"
            id="input-completed"
            labelWidth={"120px"}
          >
            <Group wrap={"nowrap"}>
              <CustomDatePicker
                sx={{ width: 190 }}
                placeholder="From Distribution Date"
                {...form.getInputProps("distribution.dateStart")}
              />
              <Text>to</Text>
              <CustomDatePicker
                sx={{ width: 190 }}
                placeholder="To Distribution Date"
                {...form.getInputProps("distribution.dateEnd")}
              />
            </Group>
          </FormInputWrapper> */}
        </Box>
        {/* </ScrollArea> */}
        <Divider />
        <Group justify="space-between" mt="lg">
          <Button
            variant="subtle"
            component="button"
            size="sm"
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
          <Button type="submit" size="sm" variant="filled">
            Apply
          </Button>
        </Group>
      </form>
    </Box>
  );
};

const statuses = Object.values(InventoryStatusTypes).map((type) => type.value);
// Each has value, label, icon, color and description for IconSelectBox.
const priorities = Object.values(PriorityTypesMap);
